/**
 * Runtime environment variable reader.
 *
 * Priority:
 *   1. window.__ENV__  — injected by nginx entrypoint.sh from K8s ConfigMap at container startup
 *   2. import.meta.env — Vite build-time env vars (local development via .env)
 *
 * This allows a single Docker image to run in any environment without rebuilding.
 */

declare global {
  interface Window {
    __ENV__?: {
      API_URL?: string;
      PATIENT_WEB_URL?: string;
      SHOW_DEV_TEST_ACCOUNTS?: string;
    };
  }
}

type EnvKey = 'API_URL' | 'PATIENT_WEB_URL' | 'SHOW_DEV_TEST_ACCOUNTS';

const URL_ENV_KEYS = new Set<EnvKey>(['API_URL', 'PATIENT_WEB_URL']);

const PLACEHOLDER_PATTERN = /PENDING_/i;

const RUNTIME_HOST_MAP: Record<
  string,
  {
    apiUrl: string;
    patientWebUrl: string;
  }
> = {
  'dev.ti-ecoa.com': {
    apiUrl: 'https://dev-api.ti-ecoa.com',
    patientWebUrl: 'https://dev-patient.ti-ecoa.com',
  },
  'dev-patient.ti-ecoa.com': {
    apiUrl: 'https://dev-api.ti-ecoa.com',
    patientWebUrl: 'https://dev-patient.ti-ecoa.com',
  },
  'edu.ti-ecoa.com': {
    apiUrl: 'https://edu-api.ti-ecoa.com',
    patientWebUrl: 'https://edu-patient.ti-ecoa.com',
  },
  'edu-patient.ti-ecoa.com': {
    apiUrl: 'https://edu-api.ti-ecoa.com',
    patientWebUrl: 'https://edu-patient.ti-ecoa.com',
  },
  'uat.ti-ecoa.com': {
    apiUrl: 'https://uat-api.ti-ecoa.com',
    patientWebUrl: 'https://uat-patient.ti-ecoa.com',
  },
  'uat-patient.ti-ecoa.com': {
    apiUrl: 'https://uat-api.ti-ecoa.com',
    patientWebUrl: 'https://uat-patient.ti-ecoa.com',
  },
  'dev.ecoa.ai-do.co.kr': {
    apiUrl: 'https://dev-api.ecoa.ai-do.co.kr',
    patientWebUrl: 'https://dev-patient.ecoa.ai-do.co.kr',
  },
  'dev-patient.ecoa.ai-do.co.kr': {
    apiUrl: 'https://dev-api.ecoa.ai-do.co.kr',
    patientWebUrl: 'https://dev-patient.ecoa.ai-do.co.kr',
  },
  'edu.ecoa.ai-do.co.kr': {
    apiUrl: 'https://edu-api.ecoa.ai-do.co.kr',
    patientWebUrl: 'https://edu-patient.ecoa.ai-do.co.kr',
  },
  'edu-patient.ecoa.ai-do.co.kr': {
    apiUrl: 'https://edu-api.ecoa.ai-do.co.kr',
    patientWebUrl: 'https://edu-patient.ecoa.ai-do.co.kr',
  },
  'uat2.ecoa.ai-do.co.kr': {
    apiUrl: 'https://api2.ecoa.ai-do.co.kr',
    patientWebUrl: 'https://ecoa2.ai-do.co.kr',
  },
  'ecoa2.ai-do.co.kr': {
    apiUrl: 'https://api2.ecoa.ai-do.co.kr',
    patientWebUrl: 'https://ecoa2.ai-do.co.kr',
  },
};

function normalizeUrl(value: string): string {
  return value.trim().replace(/\/$/, '');
}

function normalizeValue(key: EnvKey, value: string): string {
  return URL_ENV_KEYS.has(key) ? normalizeUrl(value) : value.trim();
}

function isUsableRuntimeValue(key: EnvKey, value: string | undefined): value is string {
  if (!value) return false;
  const normalized = normalizeValue(key, value);
  if (!normalized) return false;
  if (PLACEHOLDER_PATTERN.test(normalized)) return false;
  if (URL_ENV_KEYS.has(key) && window.location.protocol === 'https:' && normalized.startsWith('http://')) return false;
  return true;
}

function getHostFallback(key: EnvKey): string {
  if (key === 'SHOW_DEV_TEST_ACCOUNTS') {
    return window.location.hostname === 'uat.ti-ecoa.com' ? 'false' : 'true';
  }

  const mapped = RUNTIME_HOST_MAP[window.location.hostname];
  if (mapped) {
    return key === 'API_URL' ? mapped.apiUrl : mapped.patientWebUrl;
  }

  if (key === 'PATIENT_WEB_URL') {
    return `${window.location.protocol}//${window.location.hostname}:43013`;
  }

  return '';
}

export function getEnv(key: EnvKey): string {
  const runtimeValue = window.__ENV__?.[key];
  if (isUsableRuntimeValue(key, runtimeValue)) {
    return normalizeValue(key, runtimeValue);
  }

  const buildValue = import.meta.env[`VITE_${key}`];
  if (isUsableRuntimeValue(key, buildValue)) {
    return normalizeValue(key, buildValue);
  }

  return getHostFallback(key);
}

/**
 * Returns the base URL for the patient web app.
 * In K8s: comes from PATIENT_WEB_URL ConfigMap value (e.g. https://patient.example.com)
 * In local dev: derives from current hostname by replacing port 43012 → 43013
 */
export function getPatientWebUrl(): string {
  const configured = getEnv('PATIENT_WEB_URL');
  if (configured) return configured.replace(/\/$/, '');
  // Local development fallback
  return `${window.location.protocol}//${window.location.hostname}:43013`;
}

export function getEnvBoolean(key: Extract<EnvKey, 'SHOW_DEV_TEST_ACCOUNTS'>): boolean {
  const raw = getEnv(key).trim().toLowerCase();
  return raw === '1' || raw === 'true' || raw === 'yes' || raw === 'on';
}
